import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { articlesApi } from '../services/articles';
import { Article } from '../types/article';

// Import local images
import treatmentRoom1 from '../assets/images/article-detail/treatment-room1.png';
import treatmentRoom2 from '../assets/images/article-detail/treatment-room2.png';
import relatedArticle1 from '../assets/images/article-detail/related-article1.png';
import cloutLogo from '../assets/logos/clout-logo.png';

// Icons
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 19L14.65 14.65"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NotificationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.6667 2.66667H3.33333C2.59695 2.66667 2 3.26362 2 4V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V4C14 3.26362 13.403 2.66667 12.6667 2.66667Z"
      stroke="#767472"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.6667 1.33334V4.00001"
      stroke="#767472"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.33333 1.33334V4.00001"
      stroke="#767472"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 6.66667H14"
      stroke="#767472"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TagIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1667 12.005 21.1667C11.7422 21.1667 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 7H7.01"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShareIcon = ({ className = '' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 6.65685 16.3431 8 18 8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 15C7.65685 15 9 13.6569 9 12C9 10.3431 7.65685 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18 22C19.6569 22 21 20.6569 21 19C21 17.3431 19.6569 16 18 16C16.3431 16 15 17.3431 15 19C15 20.6569 16.3431 22 18 22Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.59 13.51L15.42 17.49"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.41 6.51L8.59 10.49"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.75 13.5L11.25 9L6.75 4.5"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InstagramIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="20" height="20" rx="5" stroke="#1f1f1f" strokeWidth="2" />
    <path
      d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5931 15.1514 13.8416 15.5297C13.0901 15.9079 12.2384 16.0396 11.4078 15.9059C10.5771 15.7723 9.80976 15.3801 9.21484 14.7852C8.61991 14.1902 8.22773 13.4229 8.09408 12.5922C7.96043 11.7616 8.09208 10.9099 8.47034 10.1584C8.84859 9.40685 9.45419 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87658 12.63 8C13.4789 8.12588 14.2649 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 6.5H17.51"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArticleDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const articleId = Number(id);
    if (!id || Number.isNaN(articleId)) {
      setArticle(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    articlesApi
      .getArticle(articleId)
      .then((data) =>
        // GET /api/articles/{id} omits content; the slug endpoint returns the full article
        !data.content && data.slug
          ? articlesApi.getArticleBySlug(data.slug).catch(() => data)
          : data
      )
      .then((data) => {
        if (!cancelled) setArticle(data);
      })
      .catch(() => {
        if (!cancelled) setArticle(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const tags = article
    ? (article.tags?.length
        ? article.tags
        : (article.treatment_categories || []).map((c) => c.replace(/_/g, ' '))
      ).join(', ')
    : '';
  const publishedDate = article?.published_at
    ? new Date(article.published_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  const paragraphs = (article?.content || article?.excerpt || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#fcfaf8]">
      {/* Header */}
      <header className="bg-[#f8f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-10 sm:h-14" />
            </div>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-lg mx-8">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search for providers"
                  className="w-full px-4 py-2 pr-10 text-sm font-['Open_Sans'] text-[#1f1f1f] placeholder-[#767472] bg-white border border-[#c6a664] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c6a664] focus:border-transparent"
                />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <SearchIcon />
                </button>
              </div>
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
                <NotificationIcon />
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <UserIcon />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-t border-b border-[#eaeaea]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center overflow-x-auto no-scrollbar">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              HOME
            </button>
            <button
              onClick={() => navigate('/endorsements')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              ENDORSEMENTS
            </button>
            <button
              onClick={() => navigate('/whats-hot')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-white uppercase bg-[#1f1f1f] whitespace-nowrap min-h-[44px]"
            >
              WHAT'S HOT!
            </button>
            <button
              onClick={() => navigate('/dashboard/analytics')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              ANALYTICS
            </button>
          </div>
        </div>
      </nav>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
        </div>
      ) : !article ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 py-16 text-center">
          <p className="font-['Open_Sans'] text-[#767472] mb-4">Article not found</p>
          <button
            onClick={() => navigate('/whats-hot')}
            className="text-[#c6a664] hover:underline"
          >
            Back to What&apos;s Hot
          </button>
        </div>
      ) : (
        <>
          {/* Article Hero Section */}
          <div className="bg-[#f8f6f2] py-6 sm:py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
              <h1 className="font-['Ovo'] text-2xl sm:text-4xl lg:text-5xl text-black uppercase mb-4 sm:mb-8">
                {article.title}
              </h1>

              {/* Article Image */}
              <div className="relative h-[200px] sm:h-[350px] lg:h-[503px] w-full mb-4 sm:mb-8 overflow-hidden">
                <img
                  src={article.featured_image_url || treatmentRoom1}
                  alt={article.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              {/* Article Meta */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-14">
                  {tags && (
                    <div className="flex items-center gap-2">
                      <TagIcon />
                      <span className="font-['Open_Sans'] text-sm sm:text-base lg:text-lg text-[#1f1f1f]">
                        {tags}
                      </span>
                    </div>
                  )}
                  {publishedDate && (
                    <div className="flex items-center gap-2">
                      <CalendarIcon />
                      <span className="font-['Open_Sans'] text-sm sm:text-base lg:text-lg text-black">
                        {publishedDate}
                      </span>
                    </div>
                  )}
                </div>
                <button className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 bg-[#1f1f1f] border border-[#1f1f1f] text-[#f8f6f2] hover:bg-white hover:text-[#1f1f1f] transition-colors group min-h-[44px] w-full sm:w-auto">
                  <ShareIcon className="text-[#f8f6f2] group-hover:text-[#1f1f1f]" />
                  <span className="font-['Ovo'] text-base sm:text-lg lg:text-xl uppercase">SHARE</span>
                </button>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 py-8 sm:py-12">
            <div className="space-y-6 sm:space-y-8">
              {/* Body (rendered as plain text paragraphs) */}
              <section className="space-y-4 sm:space-y-6">
                {paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className="font-['Open_Sans'] font-light text-sm sm:text-base text-[#1f1f1f] whitespace-pre-line"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>

              {/* Divider */}
              <div className="border-t border-[#8e8e93] my-6 sm:my-8"></div>
              {/* Related Reads */}
              <section>
                <h2 className="font-['Ovo'] text-2xl sm:text-3xl text-black uppercase mb-4 sm:mb-6">RELATED READS</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Related Article 1 */}
                  <div className="bg-white p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 border border-[#eaeaea] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.04)]">
                    <img
                      src={relatedArticle1}
                      alt="CLOUT Certified article"
                      className="w-full sm:w-32 h-48 sm:h-32 object-cover"
                    />
                    <div className="flex flex-col justify-between flex-1">
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <CalendarIcon />
                          <span className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Apr 8, 2023</span>
                        </div>
                        <h3 className="font-['Ovo'] text-lg sm:text-xl text-[#333333] uppercase mb-2">
                          CLOUT CERTIFIED: WHAT IT MEANS & WHY IT MATTERS
                        </h3>
                        <p className="font-['Open_Sans'] font-light text-sm sm:text-base text-[#333333] line-clamp-2 sm:line-clamp-none">
                          From skin tightening to bio-fillers, discover the non-surgical treatments
                          gaining traction this year.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Related Article 2 */}
                  <div className="bg-white p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 border border-[#eaeaea] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.04)]">
                    <img
                      src={treatmentRoom2}
                      alt="How Top Providers Earn Endorsements"
                      className="w-full sm:w-32 h-48 sm:h-32 object-cover"
                    />
                    <div className="flex flex-col justify-between flex-1">
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <CalendarIcon />
                          <span className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Apr 8, 2023</span>
                        </div>
                        <h3 className="font-['Ovo'] text-lg sm:text-xl text-[#333333] uppercase mb-2">
                          HOW TOP PROVIDERS EARN ENDORSEMENTS
                        </h3>
                        <p className="font-['Open_Sans'] font-light text-sm sm:text-base text-[#333333] line-clamp-2 sm:line-clamp-none">
                          From skin tightening to bio-fillers, discover the non-surgical treatments
                          gaining traction this year.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </>
      )}

      {/* CTA Section */}
      <div className="bg-[#c6a664] py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 text-center">
          <h2 className="font-['Ovo'] text-xl sm:text-2xl lg:text-3xl text-white uppercase mb-4 sm:mb-6">
            WANT TO GROW YOUR AESTHETIC BRAND?
          </h2>
          <button className="px-6 sm:px-8 py-2.5 sm:py-3 border border-white text-white font-['Ovo'] text-base sm:text-lg lg:text-xl uppercase hover:bg-white hover:text-[#c6a664] transition-colors min-h-[44px]">
            CHECK YOUR CLOUT SCORE
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#f8f6f2] py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
          <div className="flex flex-col sm:flex-row justify-between gap-8 sm:gap-12">
            {/* Logo and Social */}
            <div className="w-full sm:w-auto">
              <div className="mb-6 sm:mb-8">
                <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-16 sm:h-24" />
              </div>
              <div className="flex gap-3 sm:gap-4">
                <button className="w-12 sm:w-14 h-12 sm:h-14 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                  <FacebookIcon />
                </button>
                <button className="w-12 sm:w-14 h-12 sm:h-14 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                  <InstagramIcon />
                </button>
              </div>
            </div>

            {/* Footer Links */}
            <div className="space-y-3 sm:space-y-4">
              <button className="flex items-center gap-2 font-['Ovo'] text-sm sm:text-base lg:text-xl text-[#1f1f1f] uppercase hover:text-[#c6a664] transition-colors min-h-[44px]">
                <ArrowRightIcon /> FIND A PROVIDER
              </button>
              <button className="flex items-center gap-2 font-['Ovo'] text-sm sm:text-base lg:text-xl text-[#1f1f1f] uppercase hover:text-[#c6a664] transition-colors min-h-[44px]">
                <ArrowRightIcon /> BECOME CLOUT CERTIFIED
              </button>
              <button className="flex items-center gap-2 font-['Ovo'] text-sm sm:text-base lg:text-xl text-[#1f1f1f] uppercase hover:text-[#c6a664] transition-colors min-h-[44px]">
                <ArrowRightIcon /> ENDORSE A PROVIDER
              </button>
              <button className="flex items-center gap-2 font-['Ovo'] text-sm sm:text-base lg:text-xl text-[#1f1f1f] uppercase hover:text-[#c6a664] transition-colors min-h-[44px]">
                <ArrowRightIcon /> WHO WE ARE
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleDetail;
